import type { Level, LevelPerk } from '@/types';

/** Renewal discounts stop at 10% (Ahmed, 19 Sept 2026): a renewal discount is a permanent
 *  annuity; a store discount costs once per sale. */
export const MAX_RENEWAL_DISCOUNT = 10;
export const MAX_STORE_DISCOUNT = 20;

/** The level a total reaches — the highest pointsRequired at or below it. */
export function levelFor(levels: Pick<Level, 'pointsRequired'>[], totalScore: number) {
    return [...levels].sort((a, b) => a.pointsRequired - b.pointsRequired)
        .filter(l => totalScore >= l.pointsRequired).pop() ?? null;
}

/** The discount a member's level gives on the store or on renewals — what checkout applies. */
export function discountFor(levels: Level[], totalScore: number, appliesTo: 'store' | 'renewal'): number {
    const perks = levelFor(levels, totalScore) as Level | null;
    const cap = appliesTo === 'renewal' ? MAX_RENEWAL_DISCOUNT : MAX_STORE_DISCOUNT;
    const best = (perks?.perks ?? []).reduce((m, p) => p.kind === 'discount' && p.appliesTo === appliesTo ? Math.max(m, p.percent) : m, 0);
    return Math.min(best, cap);
}

/** Problems that would make a perk unusable or break a decision — shown before saving. */
export function perkProblems(perks: LevelPerk[]): string[] {
    const out: string[] = [];
    for (const p of perks) {
        if (p.kind === 'discount') {
            if (!(p.percent > 0)) out.push('discount_percent_missing');
            if (p.appliesTo === 'renewal' && p.percent > MAX_RENEWAL_DISCOUNT) out.push('renewal_over_cap');
            if (p.appliesTo === 'store' && p.percent > MAX_STORE_DISCOUNT) out.push('store_over_cap');
        } else if (!p.label.trim()) out.push('label_missing');
    }
    return [...new Set(out)];
}

/** One perk as a member reads it, in their language. */
export const perkText = (p: LevelPerk, t: (k: string, o?: Record<string, unknown>) => string, lang: string) =>
    p.kind === 'discount'
        ? t(`settings.gamification_tab.perks.discount_text_${p.appliesTo}`, { percent: p.percent })
        : (lang.startsWith('ar') && p.labelAr ? p.labelAr : p.label);
